import type { jsPDF } from "jspdf";

import type { SalesLeadBrief, SalesOrgLeadDetail } from "@xop/dashboard-shared";

import {
  CONTENT_W,
  MARGIN,
  PAGE_H,
  PdfWriter,
  type RGB,
  BAD,
  FAINT,
  GOOD,
  INK,
  MUTED,
  NAVY,
  TRACK,
  WARN,
  lineHeight,
  newA4Doc,
  parseMarkdownBlocks,
  sanitize,
} from "@/lib/pdf";

import { contactInfo } from "./contacts.js";
import { statusLabel } from "./leadStatus.js";
import { passSummary, qualityChecks, scoreBand, verdictMeta } from "./quality.js";

/**
 * Lead PDF generation — a TRUE VECTOR document drawn with jsPDF text/draw
 * primitives (NOT an html2canvas raster of the page).
 *
 * Why vector, not a screenshot: the old approach rasterised the live, wide,
 * two-column web layout and sliced the tall bitmap into equal A4 bands. That
 * produced (a) tiny fonts — a ~1300px desktop layout shrunk to 210mm — (b)
 * page breaks that cut straight through a section, and (c) soft, non-selectable
 * text. Drawing the brief directly gives crisp text at any zoom, readable font
 * sizes we control, real page breaks that never split a row, a tiny file, and
 * it runs identically in the Tauri desktop WebKit shell (no html2canvas, no
 * browser print-to-PDF dependency).
 *
 * The Export PDF download and the Share-via-email attachment both call
 * `buildLeadPdf`, so the two artifacts stay byte-for-byte identical.
 *
 * The page geometry, palette, WinAnsi `sanitize`, markdown parser, and the
 * paginating `PdfWriter` are the SHARED engine in `@/lib/pdf` — the same one
 * the documentation "Download PDF" builder uses. The content + helpers
 * (`contactInfo`, `qualityChecks`, `verdictMeta`, `scoreBand`, `statusLabel`)
 * are the SAME pure modules the on-screen lead page uses, so the PDF can never
 * drift from what the operator sees. The layout + `leadPdfFilename` are
 * unit-tested in `leadPdf.test.ts`.
 */

/** Filename for the attachment / download — a safe slug + `-lead-brief.pdf`. */
export function leadPdfFilename(lead: Pick<SalesOrgLeadDetail, "name">): string {
  const slug = (lead.name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return slug ? `${slug}-lead-brief.pdf` : "lead-brief.pdf";
}

/**
 * Build the lead-brief PDF document. Returned as a `jsPDF` instance so tests can
 * assert structure (page count); `buildLeadPdf` wraps it to a Blob.
 */
export function buildLeadPdfDoc(lead: SalesOrgLeadDetail): jsPDF {
  const w = new PdfWriter(newA4Doc());
  const doc = w.doc;

  /** Render a strategy-markdown blob as paginated text (lead grammar:
   * `##`/`###` headings, no fenced code, no rules). */
  const markdown = (src: string): void => {
    for (const b of parseMarkdownBlocks(src, { headingLevels: [2, 3], fences: false, rules: false })) {
      if (b.kind === "h") {
        w.gap(1.5);
        w.write(b.text, { size: 10.5, style: "bold", color: NAVY });
        w.gap(0.5);
      } else if (b.kind === "li") {
        // Render the item line-by-line so the marker stays glued to the FIRST
        // line even when the text wraps or the item crosses a page boundary
        // (drawing the marker after a wrapping write() landed it on the wrong
        // page). Marker in the gutter, text in an indented column.
        const indent = 5;
        const lh = lineHeight(10);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const lines = doc.splitTextToSize(sanitize(b.text), CONTENT_W - indent) as string[];
        lines.forEach((ln, idx) => {
          w.need(lh);
          w.y += lh;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          if (idx === 0) {
            w.setColor(MUTED);
            doc.text(b.ordinal, MARGIN, w.y);
          }
          w.setColor(INK);
          doc.text(ln, MARGIN + indent, w.y);
        });
        w.gap(0.5);
      } else if (b.kind === "p") {
        w.write(b.text, { size: 10 });
        w.gap(1.5);
      }
    }
  };

  // ── Header: org identity ──────────────────────────────────────────────
  w.write(lead.name || "Untitled lead", { size: 18, style: "bold", color: NAVY });
  w.gap(1);
  const metaBits = [
    lead.country || null,
    lead.vertical || null,
    `${Math.round((lead.confidence ?? 0) * 100)}% confidence`,
    lead.heat ? `Heat: ${lead.heat}` : null,
    statusLabel(lead.status),
  ].filter(Boolean) as string[];
  w.write(metaBits.join("  ·  "), { size: 9.5, color: MUTED });
  if (lead.domain) w.write(lead.domain, { size: 9.5, color: MUTED });
  w.gap(0.5);
  w.write(
    `Generated by the XOP Networks autonomous sales agent · ${new Date().toLocaleString()}`,
    { size: 8, color: FAINT },
  );

  // ── Verdict + recommended bundle ──────────────────────────────────────
  const verdict = verdictMeta(lead.eval_verdict);
  const verdictColor = verdict.tone === "good" ? GOOD : verdict.tone === "warn" ? WARN : verdict.tone === "bad" ? BAD : MUTED;
  w.gap(5);
  w.need(lineHeight(13) + 3);
  // status dot
  w.y += lineHeight(13);
  doc.setFillColor(verdictColor[0], verdictColor[1], verdictColor[2]);
  doc.circle(MARGIN + 1.6, w.y - 1.4, 1.6, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  w.setColor(verdictColor);
  doc.text(`${verdict.label} — ${Math.round((lead.confidence ?? 0) * 100)}% confidence`, MARGIN + 5, w.y);
  w.gap(0.5);
  w.write(verdict.blurb, { size: 9, color: MUTED });
  if (lead.recommended_bundle) {
    w.gap(1);
    doc.setFont("helvetica", "bold");
    w.write(`Recommended bundle: ${lead.recommended_bundle}`, { size: 10, style: "bold", color: INK });
  }

  // ── TL;DR ─────────────────────────────────────────────────────────────
  const tldr = (lead.agent_summary ?? "").trim();
  if (tldr) {
    w.gap(3);
    w.write(tldr, { size: 11, color: INK });
  }

  // ── Why this lead qualifies ───────────────────────────────────────────
  const checks = qualityChecks(lead);
  const { passed, total } = passSummary(checks);
  w.section("Why this lead qualifies");
  w.write(`${passed}/${total} checks passed`, { size: 9, style: "bold", color: passed === total ? GOOD : passed >= total - 1 ? WARN : BAD });
  w.gap(1.5);

  const hasScores = lead.eval_grounding != null || lead.eval_fit != null || lead.eval_need != null;
  if (hasScores) {
    const meters: Array<{ label: string; score: number | null | undefined }> = [
      { label: "Evidence grounding", score: lead.eval_grounding },
      { label: "Product fit", score: lead.eval_fit },
      { label: "Current need", score: lead.eval_need },
    ];
    for (const m of meters) {
      const band = scoreBand(m.score);
      const tone = band.tone === "good" ? GOOD : band.tone === "warn" ? WARN : band.tone === "bad" ? BAD : MUTED;
      const pct = m.score == null ? 0 : Math.max(0, Math.min(100, Math.round(m.score * 100)));
      w.need(8);
      w.y += lineHeight(9.5);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      w.setColor(INK);
      doc.text(m.label, MARGIN, w.y);
      const right = `${pct} / 100 · ${band.label}`;
      doc.setTextColor(tone[0], tone[1], tone[2]);
      doc.text(right, MARGIN + CONTENT_W - doc.getTextWidth(right), w.y);
      // meter bar
      const barY = w.y + 1.6;
      const barW = CONTENT_W;
      doc.setFillColor(TRACK[0], TRACK[1], TRACK[2]);
      doc.roundedRect(MARGIN, barY, barW, 1.8, 0.9, 0.9, "F");
      if (pct > 0) {
        doc.setFillColor(tone[0], tone[1], tone[2]);
        doc.roundedRect(MARGIN, barY, Math.max(1.8, (barW * pct) / 100), 1.8, 0.9, 0.9, "F");
      }
      w.y = barY + 1.8;
      w.gap(2.5);
    }
  }

  if (lead.eval_reason) {
    w.gap(1);
    w.write("INDEPENDENT QUALITY REVIEW", { size: 8, style: "bold", color: FAINT });
    w.write(`"${lead.eval_reason}"`, { size: 10, style: "italic", color: MUTED });
    w.gap(1);
  }

  // Checklist — each row is atomic (its dot + label + detail never splits).
  for (const c of checks) {
    const tone = c.state === "pass" ? GOOD : c.state === "warn" ? WARN : BAD;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    const labelLines = doc.splitTextToSize(sanitize(c.label), CONTENT_W - 6) as string[];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const detailLines = doc.splitTextToSize(sanitize(c.detail), CONTENT_W - 6) as string[];
    const blockH = labelLines.length * lineHeight(10) + detailLines.length * lineHeight(9) + 2;
    w.need(blockH);
    const dotY = w.y + lineHeight(10);
    doc.setFillColor(tone[0], tone[1], tone[2]);
    doc.circle(MARGIN + 1.4, dotY - 1.3, 1.4, "F");
    w.write(c.label, { x: MARGIN + 6, width: CONTENT_W - 6, size: 10, style: "bold", color: INK });
    w.write(c.detail, { x: MARGIN + 6, width: CONTENT_W - 6, size: 9, color: MUTED });
    w.gap(2);
  }

  const brief: SalesLeadBrief | undefined = lead.briefs?.[0];

  // ── XOP angle ─────────────────────────────────────────────────────────
  if (brief?.xop_angle?.trim()) {
    w.section("XOP angle");
    markdown(brief.xop_angle);
  }

  // ── Why now — full brief ──────────────────────────────────────────────
  if (brief?.why_now_markdown?.trim()) {
    w.section(`Why now — full brief v${brief.version}`);
    markdown(brief.why_now_markdown);
  } else if (lead.agent_full_reasoning?.trim()) {
    w.section("Why the agent surfaced this");
    for (const para of lead.agent_full_reasoning.split(/\n{2,}/)) {
      if (para.trim()) { w.write(para.trim(), { size: 10 }); w.gap(1.5); }
    }
  }

  // ── Suggested first message ───────────────────────────────────────────
  if (brief?.suggested_first_message?.trim()) {
    w.section("Suggested first message");
    markdown(brief.suggested_first_message);
    w.gap(1);
    w.write("Generated by the agent. Adapt freely — the trigger event is the anchor; the rest is editable.", { size: 8, color: FAINT, style: "italic" });
  }

  // ── Evidence ──────────────────────────────────────────────────────────
  const pinned = brief?.evidence ?? [];
  const signals = lead.signals ?? [];
  if (pinned.length > 0 || signals.length > 0 || (lead.signal_kinds ?? []).length > 0) {
    w.section(`Evidence (${pinned.length + signals.length})`);
    if (pinned.length > 0) {
      w.write("PINNED BY THE BRIEF", { size: 8, style: "bold", color: FAINT });
      for (const e of pinned) {
        const label = e.title || e.url;
        const line = e.url && e.title ? `${label}  (${e.url})` : label;
        w.write(line, { x: MARGIN + 4, width: CONTENT_W - 4, size: 9.5, color: INK });
      }
      w.gap(1.5);
    }
    if ((lead.signal_kinds ?? []).length > 0) {
      w.write(`Signal kinds: ${(lead.signal_kinds ?? []).join(", ")}`, { size: 9, color: MUTED });
      w.gap(1);
    }
    for (const s of signals) {
      const when = formatSignalDate(s.captured_at);
      const head = [s.kind, s.source_title || s.source_url, when].filter(Boolean).join("  ·  ");
      // Header line for the signal kept atomic with at least itself.
      w.need(lineHeight(9.5));
      w.write(head, { x: MARGIN + 4, width: CONTENT_W - 4, size: 9.5, style: "bold", color: INK });
      if (s.source_url) w.write(s.source_url, { x: MARGIN + 4, width: CONTENT_W - 4, size: 8, color: FAINT });
      if (s.summary) w.write(s.summary, { x: MARGIN + 4, width: CONTENT_W - 4, size: 9, color: MUTED });
      w.gap(2);
    }
  }

  // ── Contact ───────────────────────────────────────────────────────────
  const contact = contactInfo(lead);
  if (contact.hasAny) {
    w.section("Contact");
    if (contact.website) w.write(`Website:  ${contact.website}`, { size: 10, color: INK });
    if (contact.emails.length > 0) {
      const label = contact.emails.length === 1 ? "Email" : "Emails";
      w.write(`${label}:  ${contact.emails.map((e) => e.email).join(", ")}`, { size: 10, color: INK });
    }
    if (contact.phones.length > 0) {
      const label = contact.phones.length === 1 ? "Phone" : "Phones";
      w.write(`${label}:  ${contact.phones.join(", ")}`, { size: 10, color: INK });
    }
    if (contact.people.length > 0) {
      w.gap(1);
      w.write("KEY CONTACTS", { size: 8, style: "bold", color: FAINT });
      for (const p of contact.people) {
        const head = [p.name, p.title].filter(Boolean).join(" · ");
        const reach = [p.email, p.phone, p.linkedin, p.source].filter(Boolean).join("  ·  ");
        const headLines = (doc.setFont("helvetica", "bold"), doc.setFontSize(9.5), doc.splitTextToSize(sanitize(head), CONTENT_W - 4) as string[]);
        const reachLines = reach ? (doc.setFont("helvetica", "normal"), doc.setFontSize(8.5), doc.splitTextToSize(sanitize(reach), CONTENT_W - 4) as string[]) : [];
        w.need(headLines.length * lineHeight(9.5) + reachLines.length * lineHeight(8.5) + 1.5);
        w.write(head, { x: MARGIN + 4, width: CONTENT_W - 4, size: 9.5, style: "bold", color: INK });
        if (reach) w.write(reach, { x: MARGIN + 4, width: CONTENT_W - 4, size: 8.5, color: MUTED });
        w.gap(1.5);
      }
    }
  }

  // ── Page footers (page X of Y) ────────────────────────────────────────
  w.addPageFooters(lead.name || "Lead brief");

  return doc;
}

/** Build the lead-brief PDF as a Blob (Export download + Share attachment). */
export function buildLeadPdf(lead: SalesOrgLeadDetail): Blob {
  return buildLeadPdfDoc(lead).output("blob");
}

/** Date label for an evidence signal — short, locale-stable; falls back to the
 * raw string when the timestamp can't be parsed. */
function formatSignalDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// `RGB` is re-exported for any caller that imported it from here historically.
export type { RGB };
